import React, { useState, useEffect } from 'react';
import { Building2, Users, Trophy, BarChart3 } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { useCustomers } from '../../hooks/useCustomers';
import { usePoints } from '../../hooks/usePoints';
import LoadingSpinner from '../common/LoadingSpinner';
import ClientListModal from './ClientListModal';

const AdminDashboard = () => {
  const { companies, loading: companiesLoading } = useCompanies();
  const { customers, loading: customersLoading } = useCustomers();
  const { pointsHistory, loading: pointsLoading } = usePoints();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalCustomers: 0,
    totalPoints: 0
  });
  const [modalData, setModalData] = useState({
    isOpen: false,
    clients: [],
    title: '',
    company: null
  });

  const loading = companiesLoading || customersLoading || pointsLoading;

  useEffect(() => {
    if (!loading) {
      const activeCompanies = companies.filter(c => c.active).length;
      const totalPoints = customers.reduce((sum, c) => sum + (c.points || 0), 0);
      
      setStats({
        totalCompanies: companies.length,
        activeCompanies,
        totalCustomers: customers.length,
        totalPoints
      });
    }
  }, [companies, customers, loading]);

  const handleShowAllClients = () => {
    // Adicionar nome da empresa aos clientes
    const clientsWithCompany = customers.map(client => {
      const company = companies.find(c => c.id === client.companyId);
      return {
        ...client,
        companyName: company?.name || 'Empresa não encontrada'
      };
    });

    setModalData({
      isOpen: true,
      clients: clientsWithCompany,
      title: 'Todos os Clientes',
      company: null
    });
  };

  const handleShowCompanyClients = (company) => {
    const companyClients = customers.filter(c => c.companyId === company.id);
    
    setModalData({
      isOpen: true,
      clients: companyClients,
      title: `Clientes da Empresa`,
      company: company
    });
  };

  const closeModal = () => {
    setModalData({
      isOpen: false,
      clients: [],
      title: '',
      company: null
    });
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Dashboard Administrativo</h2>
        
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Empresas</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalCompanies}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Empresas Ativas</p>
                <p className="text-2xl font-bold text-gray-800">{stats.activeCompanies}</p>
              </div>
              <Building2 className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div 
            className="bg-white rounded-xl p-6 shadow-sm border cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all"
            onClick={handleShowAllClients}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Clientes</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalCustomers}</p>
                <p className="text-xs text-blue-600 mt-1">Clique para ver detalhes</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Pontos</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalPoints}</p>
              </div>
              <Trophy className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Empresas Recentes */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Empresas Recentes</h3>
          <div className="space-y-3">
            {companies.slice(0, 5).map(company => {
              const companyCustomers = customers.filter(c => c.companyId === company.id);
              const companyPoints = companyCustomers.reduce((sum, c) => sum + (c.points || 0), 0);
              
              return (
                <div 
                  key={company.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => handleShowCompanyClients(company)}
                >
                  <div>
                    <p className="font-medium text-gray-900">{company.name}</p>
                    <p className="text-sm text-gray-600">{company.email}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      {companyCustomers.length} cliente{companyCustomers.length !== 1 ? 's' : ''} • {companyPoints} pontos
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      company.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {company.active ? 'Ativa' : 'Inativa'}
                    </span>
                    <Users className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
            {companies.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhuma empresa cadastrada ainda
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de Lista de Clientes */}
      <ClientListModal
        isOpen={modalData.isOpen}
        onClose={closeModal}
        clients={modalData.clients}
        title={modalData.title}
        company={modalData.company}
      />
    </div>
  );
};

export default AdminDashboard;